import React from "react";
import { useLanguage } from "../../../../contexts";
import { P, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { EditableTable } from "../common/EditableTable";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";

const tomar = (inst) => ({ seus: inst.seus || [] });
const fmt = (v, d = 0) => P.fmt(v, d);

// Nivel de auditoría ASHRAE según la confiabilidad del dato de los USE (auditLevelFor del prototipo).
function nivelAuditoria(seus, t) {
  const n = seus.length;
  if (!n) return null;
  const continuas = seus.filter((s) => s.reliab === "continua").length;
  const facturas = seus.filter((s) => !s.reliab || s.reliab === "factura").length;
  if (continuas >= Math.ceil(n / 2)) return t("energy_performance_audit_level_23");
  if (facturas >= Math.ceil(n / 2)) return t("energy_performance_audit_level_1");
  return t("energy_performance_audit_level_2");
}

// Revisión energética y Usos Significativos de la Energía (USE) — cláusula 6.3 (hoja 01, filas 50, 89).
export function EnergyReviewUSE({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;

  // indicadores del análisis preliminar (PEA): EUI con el consumo de los últimos 12 meses, ECI solo con tarifa
  let eui = null;
  let eci = null;
  if (f) {
    const energia = P.energiaAnual(f);
    eui = f.area > 0 ? energia / f.area : null;
    const ch = P.costoYHuella(f);
    eci = f.area > 0 && Number.isFinite(ch.costo) ? ch.costo / f.area : null;
  }
  const totalShare = b.seus.reduce((a, u) => a + (Number(u.share) || 0), 0);
  const nivel = nivelAuditoria(b.seus, t);
  const opcionesConfiabilidad = [["factura", t("energy_performance_reliab_invoice")], ["puntual", t("energy_performance_reliab_spot")], ["continua", t("energy_performance_reliab_continuous")]];

  return (
    <div className="ep-screen">
      <h3 className="ep-screen__title">{t("energy_performance_review_title")}</h3>
      {error && <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>}

      <div className="ep-kpis">
        <div className="ep-kpi">
          <div className="ep-kpi__label">{t("energy_performance_audit_level")}</div>
          <div className="ep-kpi__value ep-kpi__value--text">{nivel || "—"}</div>
        </div>
        <div className="ep-kpi">
          <div className="ep-kpi__label">EUI · kWh/m²·{t("energy_performance_year")}</div>
          <div className="ep-kpi__value">{eui != null ? fmt(eui, 1) : t("energy_performance_pending")}</div>
          <div className="ep-kpi__sub">{t("energy_performance_eui_note")}</div>
        </div>
        <div className="ep-kpi">
          <div className="ep-kpi__label">ECI · USD/m²·{t("energy_performance_year")}</div>
          <div className="ep-kpi__value">{eci != null ? fmt(eci, 1) : t("energy_performance_pending")}</div>
          <div className="ep-kpi__sub">{eci == null ? t("energy_performance_eci_pending") : t("energy_performance_eci_note")}</div>
        </div>
      </div>

      <Section title={t("energy_performance_seus_title")} note={t("energy_performance_seus_note")}>
        <EditableTable
          key={`seus-${version}`}
          t={t}
          rows={b.seus}
          onChange={(v) => cambiar("seus", v)}
          onErrors={(n) => marcarInvalidos("seus", n)}
          newRow={() => P.ROW_TEMPLATES.seus()}
          addLabel="USE"
          emptyText={t("energy_performance_seus_empty")}
          columns={[
            { key: "name", label: "USE" },
            { key: "proceso", label: t("energy_performance_process") },
            { key: "enpi", label: t("energy_performance_seu_enpi") },
            { key: "share", label: t("energy_performance_seu_share"), type: "num" },
            { key: "reliab", label: t("energy_performance_seu_reliability"), type: "select", options: opcionesConfiabilidad },
            { key: "variables", label: t("energy_performance_seu_variables") },
            { key: "personas", label: t("energy_performance_seu_people") },
            { key: "resp", label: t("energy_performance_seu_responsible") },
            {
              key: "_63c", label: "6.3 c)", type: "view", render: (u) => {
                const falta = [!String(u.variables || "").trim() && t("energy_performance_seu_missing_variables"), !String(u.enpi || "").trim() && t("energy_performance_seu_missing_enpi"), !String(u.personas || u.resp || "").trim() && t("energy_performance_seu_missing_people")].filter(Boolean);
                return falta.length ? <StatusBadge estado="Brecha parcial">{falta.join(", ")}</StatusBadge> : <StatusBadge estado="Cumple">{t("energy_performance_complete")}</StatusBadge>;
              },
            },
          ]}
        />
        {b.seus.length > 0 && Math.abs(totalShare - 100) > 0.5 && (
          <p className="ep-section__note">{t("energy_performance_seus_share_total").replace("{n}", fmt(totalShare, 1))}</p>
        )}
      </Section>

      {f && <div className="ep-charts"><GraficoCatalogo f={f} id="use_pareto" t={t} /><GraficoCatalogo f={f} id="medicion_use" t={t} /></div>}
      <Section title={t("energy_performance_significance_title")} note={t("energy_performance_significance_note")} />

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={() => guardar(b)} onDiscard={descartar} t={t} />
    </div>
  );
}
